import { z } from "zod";
import {
  Clubhouse_LeagueType,
  Clubhouse_LeagueScoring,
  Clubhouse_LeagueFacilityScope,
  Clubhouse_LeagueWizardScheduleMode,
  Clubhouse_LeagueHandicapMethod,
  Clubhouse_LgStrokePlayRanking,
  Clubhouse_LgStrokePlayTieHandling,
  Clubhouse_LgMatchPlayScoringMode,
  Clubhouse_LgPointsLeagueMode,
} from "../../enums";
import { seasonRoundItemSchema } from "../leagueFormSchema";

// Point-value fields are non-negative; coerce so number inputs (strings) parse.
const pointNum = z.coerce.number().min(0);
const sideContest = z.object({ enabled: z.boolean(), points: pointNum });

// UI-shaped scoring policy. The mapping layer translates the custom-points and
// side-contest objects into the API's customPointRules[] / sideContests[].
export const scoringPolicySchema = z.object({
  // Handicap is applied server-side (always on); not an operator field.
  strokePlay: z
    .object({
      ranking: z.enum(Clubhouse_LgStrokePlayRanking).optional(),
      tieHandling: z.enum(Clubhouse_LgStrokePlayTieHandling).optional(),
    })
    .optional(),
  matchPlay: z
    .object({
      matchScoringMode: z.enum(Clubhouse_LgMatchPlayScoringMode).optional(),
      winPoints: pointNum.optional(),
      tiePoints: pointNum.optional(),
      lossPoints: pointNum.optional(),
      holeWonPoints: pointNum.optional(),
      holeTiedPoints: pointNum.optional(),
      holeLostPoints: pointNum.optional(),
    })
    .optional(),
  pointsLeague: z
    .object({
      mode: z.enum(Clubhouse_LgPointsLeagueMode).optional(),
      maxPointsPerHole: pointNum.optional(),
      customPoints: z
        .object({
          doubleBogey: pointNum,
          bogey: pointNum,
          par: pointNum,
          birdie: pointNum,
          eagle: pointNum,
        })
        .optional(),
    })
    .optional(),
  sideContests: z
    .object({
      closestToPin: sideContest,
      longDrive: sideContest,
      skins: sideContest,
    })
    .optional(),
});

/** Defaults matching the mocks; used to seed the form and reset on format switch. */
export const SCORING_DEFAULTS = {
  strokePlay: {
    ranking: Clubhouse_LgStrokePlayRanking.TOTAL_TO_PAR,
    tieHandling: Clubhouse_LgStrokePlayTieHandling.SHARED,
  },
  matchPlay: {
    matchScoringMode: Clubhouse_LgMatchPlayScoringMode.HEAD_TO_HEAD,
    winPoints: 1,
    tiePoints: 0.5,
    lossPoints: 0,
    holeWonPoints: 1,
    holeTiedPoints: 0.5,
    holeLostPoints: 0,
  },
  pointsLeague: {
    // Stableford vs Custom is gone — the point table is always editable, so the
    // payload always carries the custom rows (Custom mode).
    mode: Clubhouse_LgPointsLeagueMode.CUSTOM_TABLE,
    maxPointsPerHole: 10,
    customPoints: { doubleBogey: 0, bogey: 1, par: 2, birdie: 3, eagle: 4 },
  },
  // Points/Match values above are an allowed pre-filled default (still fully
  // editable); these are not — the operator must opt in, so nothing is
  // enabled until they flip a switch.
  sideContests: {
    closestToPin: { enabled: false, points: 10 },
    longDrive: { enabled: false, points: 5 },
    skins: { enabled: false, points: 8 },
  },
} as const;

/** Settings collected across the advanced tabs. Only `leagueType` is required
 *  (draft creation); every other field is optional and validated for range only
 *  if present. The Roster/Scoring tabs fill the rest in later. */
export const advancedSettingsSchema = z.object({
  leagueType: z.enum(Clubhouse_LeagueType, {
    message: "Select a league format",
  }),
  leagueTeamSize: z.coerce.number().int().min(1).max(4).optional(),
  defaultGroupSize: z.coerce.number().int().min(1).max(4).optional(),
  defaultSimGroups: z.coerce.number().int().min(1).optional(),
  allowAsynchronousTeamPlay: z.boolean().optional(),
  allowAsynchronousMatchPlay: z.boolean().optional(),
  participantCap: z.coerce.number().int().min(1).max(150).optional(),
  handicapMethod: z.enum(Clubhouse_LeagueHandicapMethod).optional(),
  leagueScoringType: z.enum(Clubhouse_LeagueScoring).optional(),
  // Required (no default): the operator must explicitly pick a tee in create
  // mode. Edit mode always has a real value by submit time via hydration.
  mensTee: z.number().int({ message: "Select a tee" }),
  womensTee: z.number().int({ message: "Select a tee" }),
});

export const advancedLeagueSchema = z
  .object({
    name: z.string().min(1, "League name is required").max(100),
    description: z.string().max(500).optional(),
    facilityId: z.string().min(1, "Facility ID is required"),
    facilityScope: z.enum(Clubhouse_LeagueFacilityScope).optional(),
    // Clubhouse ids of the facilities this league can be played at (the Location
    // tab's checklist). One = THIS_FACILITY, more than one = ANY_OF_MY_FACILITIES.
    participatingClubhouseFacilityIds: z.array(z.number()).optional(),
    allowSubstitutes: z.boolean().optional(),
    // Scoring format lives in settings.leagueScoringType; scoringPolicy carries
    // the per-format config, handicap toggle, and side contests.
    scoringPolicy: scoringPolicySchema.optional(),
    leagueStartDate: z.string().min(1, "Start date is required"),
    leagueEndDate: z.string().min(1, "End date is required"),
    leagueStartDateTimezone: z.string().optional(),
    leagueEndDateTimezone: z.string().optional(),
    seasonRounds: z.array(seasonRoundItemSchema).optional(),
    seasonRoundCount: z.coerce.number().int().min(1).max(52).optional(),
    // Fixed-time (WeeklyRounds) vs Flex (SeasonFlex). Drives round generation.
    scheduleMode: z.enum(Clubhouse_LeagueWizardScheduleMode).optional(),
    // Client-only anchor for weekly generation (0=Sun … 6=Sat); not persisted.
    weekDay: z.coerce.number().int().min(0).max(6).optional(),
    // Where a generated round's conditions come from. Client-only: it shapes
    // the rounds in `seasonRounds`, which is what actually persists. Lives on
    // the form rather than in local state so switching tabs doesn't silently
    // regenerate every round against a different source. Absent means
    // randomize, which needs no setup and is always available — a facility with
    // no presets yet still gets varied rounds rather than blank ones.
    roundSettingsSource: z.enum(["preset", "randomize"]).optional(),
    // Which saved Round Preset seeds the generated rounds. Client-only like the
    // source above: it shapes `seasonRounds`, which is what persists.
    roundPresetId: z.string().optional(),
    settings: advancedSettingsSchema,
  })
  // The backend requires the end date to be at least one calendar day after
  // the start (a same-day season returns 400), so this is a strict `<`.
  .refine(
    (d) =>
      !d.leagueStartDate ||
      !d.leagueEndDate ||
      new Date(d.leagueStartDate) < new Date(d.leagueEndDate),
    {
      message: "End date must be at least one day after the start date",
      path: ["leagueEndDate"],
    },
  )
  // Only the selected format's required sub-field is enforced; the other
  // formats' values may linger in form state but aren't validated or sent.
  .superRefine((d, ctx) => {
    const fmt = d.settings.leagueScoringType;
    if (
      fmt === Clubhouse_LeagueScoring.STROKE &&
      !d.scoringPolicy?.strokePlay?.ranking
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Select how the leaderboard ranks",
        path: ["scoringPolicy", "strokePlay", "ranking"],
      });
    }
    if (
      fmt === Clubhouse_LeagueScoring.MATCH &&
      !d.scoringPolicy?.matchPlay?.matchScoringMode
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Select a match type",
        path: ["scoringPolicy", "matchPlay", "matchScoringMode"],
      });
    }
    if (
      fmt === Clubhouse_LeagueScoring.MATCH &&
      d.settings.allowAsynchronousMatchPlay == null
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Select how opponents play",
        path: ["settings", "allowAsynchronousMatchPlay"],
      });
    }
    if (
      fmt === Clubhouse_LeagueScoring.POINTS &&
      !d.scoringPolicy?.pointsLeague?.mode
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Select a points model",
        path: ["scoringPolicy", "pointsLeague", "mode"],
      });
    }
  });

export type AdvancedLeagueFormValues = z.infer<typeof advancedLeagueSchema>;

export function isTeamFormat(leagueType?: Clubhouse_LeagueType): boolean {
  return leagueType === Clubhouse_LeagueType.TEAM;
}

// Leagues whose participants are organized into teams — Team or Scramble.
// Drives team-management UI (the Roster/Teams toggle and the Teams tab).
// Distinct from isTeamFormat, which stays TEAM-only for the create form's
// team-size fields.
export function isTeamBasedFormat(leagueType?: Clubhouse_LeagueType): boolean {
  return (
    leagueType === Clubhouse_LeagueType.TEAM ||
    leagueType === Clubhouse_LeagueType.SCRAMBLE
  );
}

export function isScrambleFormat(
  leagueType?: Clubhouse_LeagueType | null,
): boolean {
  return leagueType === Clubhouse_LeagueType.SCRAMBLE;
}
